import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, LogOut, LayoutDashboard, Settings } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const isInvoicePage = location.pathname.startsWith('/facture/') || location.pathname.startsWith('/paiement/');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const handleLogout = () => {
    logout();
    toast.success('À bientôt !');
    navigate('/');
  };

  const handleProfileClick = () => {
    setUserMenuOpen(false);
    if (user?.role === 'PRESTATAIRE') {
      navigate('/mon-profil/edit');
    } else {
      navigate('/dashboard');
    }
  };

  const navLinks = [
    { label: 'Accueil', to: '/' },
    { label: 'Services', to: '/services' },
    { label: 'Prestataires', to: '/prestataires' },
    { label: 'Comment ça marche', to: '/comment-ca-marche' },
    { label: 'Contact', to: '/contact' },
    { label: 'Qui sommes-nous', to: '/qui-sommes-nous' },

  ];

  // Liens dashboard selon le rôle
  const dashboardLinks = user?.role === 'CLIENT'
    ? [
        { label: 'Mon espace', to: '/dashboard', icon: <LayoutDashboard size={16} /> },
      ]
    : user?.role === 'PRESTATAIRE'
    ? [
        { label: 'Mon espace', to: '/espace-prestataire', icon: <LayoutDashboard size={16} /> },
        { label: 'Mon profil', to: '/mon-profil/edit', icon: <Settings size={16} /> },
      ]
    : [];

  const isScrolledOrNotHome = scrolled || !isHome;

  const Avatar = ({ size = 28 }) => {
    const photo = user?.profilePhotoUrl;
    const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;
    return photo ? (
      <img src={photo} alt={user?.firstName}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--orange)', flexShrink: 0 }} />
    ) : (
      <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: size * 0.36, flexShrink: 0 }}>
        {initials}
      </div>
    );
  };

  if (isInvoicePage) return null;

  return (
    <>
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
          padding: isScrolledOrNotHome ? '0.8rem 0' : '1.3rem 0',
          background: isScrolledOrNotHome
            ? 'rgba(26, 59, 93, 0.98)'
            : 'linear-gradient(to bottom, rgba(10,27,46,0.8), transparent)',
          backdropFilter: isScrolledOrNotHome ? 'blur(16px)' : 'none',
          borderBottom: isScrolledOrNotHome ? '1px solid rgba(255,255,255,0.1)' : 'none',
          transition: 'all 0.35s ease',
          boxShadow: isScrolledOrNotHome ? '0 4px 30px rgba(0,0,0,0.3)' : 'none',
        }}
      >
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
          {/* LOGO */}
          <Link to={isAuthenticated ? (user?.role === 'CLIENT' ? '/dashboard' : '/espace-prestataire') : '/'} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <img src="/logo_elim.jpeg" alt="ELIM DEPANNAGE" style={{ height: '38px', width: 'auto', objectFit: 'contain', borderRadius: '6px' }} />
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'white', letterSpacing: '2px', lineHeight: 1 }}>
                ELIM<span style={{ color: 'var(--orange)' }}>DEPANNAGE</span>
              </div>
              <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '2px', textTransform: 'uppercase' }}>
                Plateforme N°1 au Bénin
              </div>
            </div>
          </Link>

          {/* DESKTOP NAV */}
          <div style={{ display: 'flex', gap: '0.2rem', marginLeft: 'auto', alignItems: 'center' }} className="desktop-nav">
            {!isAuthenticated && navLinks.map(link => (
              <Link key={link.to} to={link.to}
                style={{
                  padding: '0.5rem 1rem',
                  fontFamily: 'var(--font-cond)', fontSize: '0.9rem', fontWeight: 700,
                  letterSpacing: '1px', textTransform: 'uppercase',
                  color: location.pathname === link.to ? 'var(--orange)' : 'rgba(255,255,255,0.85)',
                  borderRadius: '6px', transition: 'all 0.2s',
                  borderBottom: location.pathname === link.to ? '2px solid var(--orange)' : '2px solid transparent',
                }}
                onMouseEnter={e => e.target.style.color = 'white'}
                onMouseLeave={e => e.target.style.color = location.pathname === link.to ? 'var(--orange)' : 'rgba(255,255,255,0.85)'}
              >
                {link.label}
              </Link>
            ))}

            {/* Liens dashboard quand connecté */}
            {isAuthenticated && dashboardLinks.map(link => (
              <Link key={link.to} to={link.to}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.5rem 1rem',
                  fontFamily: 'var(--font-cond)', fontSize: '0.9rem', fontWeight: 700,
                  letterSpacing: '1px', textTransform: 'uppercase',
                  color: location.pathname === link.to ? 'var(--orange)' : 'rgba(255,255,255,0.85)',
                  borderRadius: '6px', transition: 'all 0.2s',
                  borderBottom: location.pathname === link.to ? '2px solid var(--orange)' : '2px solid transparent',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'white'}
                onMouseLeave={e => e.currentTarget.style.color = location.pathname === link.to ? 'var(--orange)' : 'rgba(255,255,255,0.85)'}
              >
                {link.icon} {link.label}
              </Link>
            ))}
          </div>

          {/* AUTH */}
          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }} className="desktop-nav">
            {isAuthenticated ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px', padding: '0.4rem 0.9rem 0.4rem 0.4rem',
                    color: 'white', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  <Avatar size={30} />
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.9rem' }}>{user?.firstName}</span>
                  <ChevronDown size={14} style={{ transition: 'transform 0.2s', transform: userMenuOpen ? 'rotate(180deg)' : 'none' }} />
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      style={{
                        position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                        background: 'white', borderRadius: '12px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        minWidth: '220px', overflow: 'hidden', zIndex: 100,
                      }}
                      onMouseLeave={() => setUserMenuOpen(false)}
                    >
                      <div style={{ padding: '1rem 1.2rem', borderBottom: '1px solid var(--gray-mid)', display: 'flex', alignItems: 'center', gap: '0.8rem', background: 'var(--gray-light)' }}>
                        <div onClick={handleProfileClick} style={{ cursor: 'pointer', position: 'relative' }}>
                          <Avatar size={40} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--blue-dark)', fontSize: '0.9rem' }}>
                            {user?.firstName} {user?.lastName}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--gray-text)' }}>
                            {user?.role === 'PRESTATAIRE' ? '🔧 Prestataire' : '👤 Client'}
                          </div>
                        </div>
                      </div>

                      <Link
                        to={user?.role === 'CLIENT' ? '/dashboard' : '/espace-prestataire'}
                        onClick={() => setUserMenuOpen(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.9rem 1.2rem', color: 'var(--blue-dark)', transition: 'background 0.15s', textDecoration: 'none' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-light)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                      >
                        <LayoutDashboard size={16} /> Mon espace
                      </Link>

                      {user?.role === 'PRESTATAIRE' && (
                        <Link
                          to="/mon-profil/edit"
                          onClick={() => setUserMenuOpen(false)}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.9rem 1.2rem', color: 'var(--blue-dark)', transition: 'background 0.15s', textDecoration: 'none', borderTop: '1px solid var(--gray-mid)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-light)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                        >
                          <Settings size={16} /> Modifier mon profil
                        </Link>
                      )}

                      <button
                        onClick={handleLogout}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.9rem 1.2rem', width: '100%', background: 'none', border: 'none', borderTop: '1px solid var(--gray-mid)', color: 'var(--error)', cursor: 'pointer', fontSize: '0.9rem', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <LogOut size={16} /> Déconnexion
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <Link to="/connexion" className="btn btn-outline-white btn-sm">Connexion</Link>
                <Link to="/inscription" className="btn btn-primary btn-sm">S'inscrire</Link>
              </>
            )}
          </div>

          {/* HAMBURGER */}
          <button onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: 'none', background: 'none', border: 'none', color: 'white', padding: '0.5rem', marginLeft: 'auto' }}
            className="hamburger-btn">
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </motion.nav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            style={{
              position: 'fixed', inset: 0, background: 'var(--blue-dark)',
              zIndex: 999, padding: '5rem 2rem 2rem',
              display: 'flex', flexDirection: 'column', gap: '0.5rem',
            }}
          >
            {isAuthenticated && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 0', marginBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div onClick={() => { setMobileOpen(false); handleProfileClick(); }} style={{ cursor: 'pointer' }}>
                  <Avatar size={48} />
                </div>
                <div>
                  <div style={{ color: 'white', fontWeight: 700 }}>{user?.firstName} {user?.lastName}</div>
                  <div style={{ color: 'var(--orange)', fontSize: '0.8rem' }}>{user?.role === 'PRESTATAIRE' ? '🔧 Prestataire' : '👤 Client'}</div>
                </div>
              </div>
            )}

            {/* Liens selon connexion */}
            {(!isAuthenticated ? navLinks : dashboardLinks).map((link, i) => (
              <motion.div key={link.to} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}>
                <Link to={link.to}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '1rem 0', fontFamily: 'var(--font-display)', fontSize: '1.8rem', letterSpacing: '2px', color: 'white', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  {link.label.toUpperCase()}
                </Link>
              </motion.div>
            ))}

            <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {isAuthenticated ? (
                <button onClick={handleLogout} className="btn btn-outline-white btn-lg btn-full">
                  <LogOut size={16} style={{ marginRight: '0.5rem' }} /> Déconnexion
                </button>
              ) : (
                <>
                  <Link to="/connexion" className="btn btn-outline-white btn-lg btn-full">Connexion</Link>
                  <Link to="/inscription" className="btn btn-primary btn-lg btn-full">S'inscrire gratuitement</Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: block !important; }
        }
      `}</style>
    </>
  );
}
